// The Close-hub "Preview report" breakdown. Deliberately JURISDICTION-NEUTRAL:
// it groups the period's already-server-assembled staged transactions by
// contribution type / expenditure category using the shared enum→label helpers.
// There is no per-state branch here, so onboarding a new jurisdiction (LA, GA, …)
// needs zero changes to the preview — the same grouping serves every filer. The
// full, per-jurisdiction schedule view (Form 3 / COMET / FCPA) is reached by
// linking to the generated report's detail page, not by rebuilding it here.

import type { ReportPreviewResponse, ReportPreviewTransaction } from '../generated/dtos.gen';
import { FINANCE_STAGED_TRANSACTION_TYPES } from '../generated/constants.gen';
import { ReceiptNature, ExpenditureCategory } from '../generated/enums.gen';
import { contributionTypeLabel } from './contributionTypeLabel';
import { expenditureCategoryLabel } from './expenditureCategoryLabel';

/** One row of the receipts / disbursements breakdown. */
export interface PreviewBreakdownGroup {
  /** The stable wire enum value — React key + test anchor (never rendered raw). */
  key: string;
  /** Treasurer-first display label (jurisdiction-aware for expenditures). */
  label: string;
  count: number;
  total: number;
}

export interface PreviewBreakdown {
  /** Receipts grouped by contribution type, largest total first. */
  contributionGroups: PreviewBreakdownGroup[];
  /** Disbursements grouped by expenditure category, largest total first. */
  expenditureGroups: PreviewBreakdownGroup[];
}

// Wire discriminator on ReportPreviewTransaction.type.
const CONTRIBUTION_KIND = FINANCE_STAGED_TRANSACTION_TYPES.Contribution;
const EXPENDITURE_KIND = FINANCE_STAGED_TRANSACTION_TYPES.Expenditure;

/**
 * Groups a period's preview transactions into receipts-by-type and
 * disbursements-by-category, each labeled at the render boundary. `rulesetCode`
 * (the campaign's `CampaignFinanceSettings.RulesetCode`, e.g. "AL") selects the
 * jurisdiction-accurate expenditure labels; omit it for the federal-flavored
 * defaults. Group subtotals foot to the same server-authored `transactions`, so
 * the sections always reconcile to the modal's period totals.
 */
export function buildPreviewBreakdown(
  preview: Pick<ReportPreviewResponse, 'transactions'>,
  rulesetCode?: string | null,
): PreviewBreakdown {
  const contributions = new Map<string, PreviewBreakdownGroup>();
  const expenditures = new Map<string, PreviewBreakdownGroup>();

  for (const t of preview.transactions) {
    if (t.type === CONTRIBUTION_KIND) {
      const key = t.contributionType ?? ReceiptNature.Other;
      accumulate(contributions, key, t, () =>
        contributionTypeLabel(key as ReceiptNature),
      );
    } else if (t.type === EXPENDITURE_KIND) {
      const key = t.expenditureCategory ?? ExpenditureCategory.Other;
      accumulate(expenditures, key, t, () =>
        expenditureCategoryLabel(key as ExpenditureCategory, rulesetCode),
      );
    }
  }

  return {
    contributionGroups: sortByTotalDesc([...contributions.values()]),
    expenditureGroups: sortByTotalDesc([...expenditures.values()]),
  };
}

function accumulate(
  into: Map<string, PreviewBreakdownGroup>,
  key: string,
  txn: ReportPreviewTransaction,
  label: () => string,
): void {
  const existing = into.get(key);
  if (existing) {
    existing.count += 1;
    existing.total += txn.amount;
    return;
  }
  into.set(key, { key, label: label(), count: 1, total: txn.amount });
}

// Largest total first; a label tie-break keeps the order stable/deterministic.
function sortByTotalDesc(groups: PreviewBreakdownGroup[]): PreviewBreakdownGroup[] {
  return groups.sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
}
